import React from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  LinearProgress,
  TextField,
} from '@mui/material';
import { t } from 'i18next';
import { useFormik } from 'formik';
import { useDispatch } from 'react-redux';
import * as yup from 'yup';
import PropTypes from 'prop-types';
import { addManageAction } from '../../../../actions/mangeActionV2';
import { showErrorToast, showSuccessToast } from '../../../../slices/toastSlice';

const validationSchema = yup.object().shape({
  name: yup.string().required('Name is required'),
  street: yup.string().required('Street is required'),
  city: yup.string().required('City is required'),
  country: yup.string().required('Country is required'),
  consigneeNumber: yup.string(),
  email: yup.string().required('Email is required').email('Invalid email'),
  phoneNumber: yup
    .string()
    .required('Phone number is required')
    .matches(/^\+\d{6,14}$/, 'Phone number is not valid. Please use the format: "+1234567890"'),
});

export default function AddConsigneeDialog({ open, onClose, onSelect, clientId }) {
  const dispatch = useDispatch();

  const formik = useFormik({
    initialValues: {
      name: '',
      street: '',
      city: '',
      country: '',
      consigneeNumber: '',
      email: '',
      phoneNumber: '',
    },
    validationSchema,
    enableReinitialize: true,
    onSubmit: async (values, { setSubmitting, resetForm }) => {
      try {
        setSubmitting(true);
        const created = await dispatch(addManageAction({ mode: 'consignee', data: values, parentId: clientId })).unwrap();
        const selected = created?.object || created?.data || created || values;

        dispatch(showSuccessToast(t('Consignee has been saved successfully')));
        if (onSelect) onSelect(selected);
        resetForm();
      } catch (err) {
        dispatch(showErrorToast(err?.message || err || t('Failed to save Consignee')));
      } finally {
        setSubmitting(false);
      }
    },
  });

  const handleClose = () => {
    if (formik.isSubmitting) return;
    formik.resetForm();
    onClose();
  };

  const fieldProps = (name) => ({
    name,
    value: formik.values[name],
    onChange: formik.handleChange,
    onBlur: formik.handleBlur,
    error: formik.touched[name] && Boolean(formik.errors[name]),
    helperText: formik.touched[name] && formik.errors[name],
  });

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>{t('Add Consignee')}</DialogTitle>
      <Box component="form" onSubmit={formik.handleSubmit}>
        <DialogContent dividers>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' },
              gap: 2,
              pt: 1,
            }}
          >
            <TextField label={t('Name')} {...fieldProps('name')} />
            <TextField label={t('Consignee Number')} {...fieldProps('consigneeNumber')} />
            <TextField label={t('Email')} {...fieldProps('email')} />
            <TextField label={t('Phone Number')} {...fieldProps('phoneNumber')} />
            <TextField label={t('Street')} {...fieldProps('street')} />
            <TextField label={t('City')} {...fieldProps('city')} />
            <TextField label={t('Country')} {...fieldProps('country')} />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 2, py: 1 }}>
          <Button onClick={handleClose} disabled={formik.isSubmitting}>
            {t('Cancel')}
          </Button>
          <Button type="submit" variant="contained" disabled={formik.isSubmitting || !clientId}>
            {t('Save')}
          </Button>
        </DialogActions>
        {formik.isSubmitting && <LinearProgress />}
      </Box>
    </Dialog>
  );
}

AddConsigneeDialog.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
  onSelect: PropTypes.func,
  clientId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};
